import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { notify } from '@/lib/notifications/engine';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { StickyNote, Pencil, Lock } from 'lucide-react';

/**
 * client#21: shift notes in the admin shift views (Scheduling Board drawer, roster drawer) — shown in full, and editable
 * in place by roster.update / roster.manage (PUT /roster/shifts/{id} { notes }). Office-only `internal_notes` are shown
 * to those editors only, never to staff.
 */
export function ShiftNotesCard({ shift, onSaved, readOnly = false }) {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canEdit = !readOnly && (perms.includes('roster.update') || perms.includes('roster.manage'));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shift?.notes || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setDraft(shift?.notes || ''); }, [shift?.notes, shift?.id, editing]);
  if (!shift) return null;

  const notes = (shift.notes || '').trim();
  const internal = canEdit ? (shift.internal_notes || '').trim() : '';

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/roster/shifts/${shift.id}`, { notes: draft.trim() });
      toast.success('Shift notes saved');
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2 text-xs space-y-1.5" data-testid="shift-notes-card">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-medium text-amber-900"><StickyNote className="h-3.5 w-3.5" /> Shift notes</span>
        {canEdit && !editing && (
          <button type="button" className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" onClick={() => setEditing(true)} data-testid="shift-notes-edit">
            <Pencil className="h-3 w-3" /> {notes ? 'Edit' : 'Add notes'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-1.5">
          <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Notes for the staff working this shift" className="text-xs" data-testid="shift-notes-input" />
          <div className="flex justify-end gap-1.5">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(false); setDraft(shift.notes || ''); }} disabled={saving}>Cancel</Button>
            <Button type="button" size="sm" className="h-7 text-xs" onClick={save} disabled={saving || draft.trim() === notes} data-testid="shift-notes-save">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      ) : notes ? (
        <p className="whitespace-pre-wrap text-slate-800" data-testid="shift-notes-text">{notes}</p>
      ) : (
        <p className="italic text-muted-foreground" data-testid="shift-notes-empty">No shift notes</p>
      )}
      {internal && (
        <p className="flex items-start gap-1.5 border-t border-amber-200 pt-1.5 text-slate-600" data-testid="shift-internal-notes">
          <Lock className="h-3 w-3 mt-0.5 shrink-0" /><span><span className="font-medium">Office notes</span> (not shown to staff): {internal}</span>
        </p>
      )}
    </div>
  );
}
